<template>
  <help-info-wrapper
    :info.sync="helpData"
    :service-name="serviceName"
    class="warp"
  >
    <v-card flat>
      <v-card-title class="d-flex justify-space-between align-center mb-4">
        <!-- <span class="text-h6 primary--text">{{ $t("game_editor") }}</span> -->

        <div class="d-flex align-center gap-3 w-100">
          <v-select
            v-model="gameType"
            :items="gameTypes"
            item-text="text"
            item-value="value"
            label="نوع بازی"
            dense
            outlined
            hide-details
            style="max-width: 250px"
            @change="onGameTypeChange"
          />

          <v-btn class="btn-edit rounded-lg px-2 left" @click="resetToDefault">
            <v-icon left small>mdi-restore</v-icon>
            {{ $t("reset_defaults") }}
          </v-btn>

          <v-btn class="btn-get-code rounded-lg px-2" @click="saveAndPreview">
            <v-icon left small>mdi-eye-check</v-icon>
            {{ $t("save_and_preview") }}
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <custom-tabs v-model="tabs" grow v-if="gameConfig && gameConfig.type">
          <template v-slot:staticTabs>
            <v-tab v-if="currentSchema.texts">
              <v-icon small left>mdi-text</v-icon>
              {{ $t("texts") }}
            </v-tab>

            <v-tab v-if="currentSchema.images">
              <v-icon small left>mdi-image</v-icon>
              {{ $t("images") }}
            </v-tab>

            <v-tab v-if="currentSchema.colors">
              <v-icon small left>mdi-palette</v-icon>
              {{ $t("colors") }}
            </v-tab>

            <v-tab v-if="currentSchema.config">
              <v-icon small left>mdi-cog</v-icon>
              {{ $t("advanced_settings") }}
            </v-tab>

            <v-tab v-if="currentSchema.gameplay">
              <v-icon small left>mdi-controller-classic</v-icon>
              {{ $t("gameplay_settings") }}
            </v-tab>

            <v-tab v-if="currentSchema.login">
              <v-icon small left>mdi-account-key</v-icon>
              {{ $t("login_settings") }}
            </v-tab>
          </template>

          <template v-slot:staticItems>
            <v-tab-item v-if="currentSchema.texts">
              <TabTexts v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="currentSchema.images">
              <TabImages v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="currentSchema.colors">
              <TabColors v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="currentSchema.config">
              <TabSetting v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="currentSchema.gameplay">
              <TabGamePlay v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="currentSchema.login">
              <TabLogin v-model="gameConfig" :schema="currentSchema" />
            </v-tab-item>

            <v-tab-item v-if="!hasAnyTab">
              <v-alert type="info" class="ma-6" prominent>
                تنظیماتی برای این بازی یافت نشد.
              </v-alert>
            </v-tab-item>
          </template>
        </custom-tabs>
        <v-alert v-else type="info" class="ma-6" color="#E1FFFA" prominent>
          در حال بارگذاری تنظیمات بازی...
        </v-alert>
      </v-card-text>
    </v-card>
  </help-info-wrapper>
</template>

<script>
import { editorSchema } from "./editorSchema.js";
import TabTexts from "./tabTexts.vue";
import TabImages from "./tabImages.vue";
import TabGamePlay from "./tabGamePlay.vue";
import TabSetting from "./tabSetting.vue";
import TabColors from "./tabColors.vue";
import TabLogin from "./tabLogin.vue"; // Import the new tab component

export default {
  name: "PublicGameEditor",
  components: {
    TabTexts,
    TabImages,
    TabGamePlay,
    TabSetting,
    TabColors,
    TabLogin,
  },
  props: {
    value: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      gameType: null,
      skipNextSync: false,
      helpData: [],
      serviceName: "Qrland",
      tabs: 0,
      gameConfig: {
        type: "Game",
        texts: {},
        images: {},
        config: {}, // مخصوص بازی اول
        gameplay: {}, // مخصوص بازی دوم
        colors: {}, // مخصوص بازی دوم
        login: {}, // Login settings
      },
    };
  },

  computed: {
    currentSchema() {
      return editorSchema[this.gameConfig?.type] || {};
    },
    gameTypes() {
      return [
        { text: "Runner V2 (آهنربایی)", value: "Game" },
        { text: "MeepMeep (سه لاینه)", value: "MeepMeepProRunner" },
      ];
    },
    hasAnyTab() {
      const s = this.currentSchema;
      return (
        s.texts || s.images || s.colors || s.config || s.gameplay || s.login
      );
    },
    // اصلاح باگ دسترسی به value
    valueComputed() {
      return this.value || {};
    },
  },

  watch: {
    gameConfig: {
      deep: true,
      handler() {
        if (this.skipNextSync) {
          this.skipNextSync = false;
          return;
        }
        this.syncToParent();
      },
    },
    value: {
      deep: true,
      immediate: true,
      handler(newVal) {
        const config = newVal?.previewer?.game?.config;
        if (!config || Object.keys(config).length === 0) {
          if (!this.gameType) this.setInitConfig("Game");
          return;
        }
        this.skipNextSync = true;
        // Deep clone to avoid reference issues
        this.gameConfig = JSON.parse(JSON.stringify(config));
        this.gameType = config.type;

        // Ensure all required properties exist
        if (!this.gameConfig.texts) this.$set(this.gameConfig, "texts", {});
        if (!this.gameConfig.images) this.$set(this.gameConfig, "images", {});
        if (!this.gameConfig.config) this.$set(this.gameConfig, "config", {});
        if (!this.gameConfig.gameplay)
          this.$set(this.gameConfig, "gameplay", {});
        if (!this.gameConfig.colors) this.$set(this.gameConfig, "colors", {});
        if (!this.gameConfig.login) this.$set(this.gameConfig, "login", {});
      },
    },
  },

  mounted() {
    if (!this.value?.previewer?.game?.config?.type) {
      this.setInitConfig("Game");
    }
  },

  methods: {
    setInitConfig(type) {
      const selectedType = type || "Game";
      this.gameType = selectedType;

      const DEFAULTS = {
        Game: {
          type: "Game",
          texts: {
            gameTitle: "مأموریت کیوآرلند",
            tutorialTitle: "راهنمای بازی",
            tutorialSteps: [
              "درگ برای حرکت",
              "اسکن کیوآرها",
              "دوری از موانع",
              "استفاده از آهنربا",
            ],
            tutorialBtn: "فهمیدم",
            scoreLabel: "امتیاز",
            magnetLabel: "آهنربا فعال شد!",
            winMsg: "عالی بود!",
            failMsg: "دوباره تلاش کن",
            startBtn: "شروع",
            submitBtn: "ثبت امتیاز",
            restartBtn: "تلاش مجدد",
            phoneTitle: "دریافت جایزه",
            phonePlaceholder: "شماره موبایل...",
            confirmBtn: "تایید",
            backBtn: "بازگشت",
          },
          images: {
            bgMain: "",
            bgModal: "",
            player: "",
            playerHappy: "",
            playerSad: "",
            magnet: "",
            qrs: [],
            traps: [],
            phoneFrameBackground: "", // Added for phone frame background
          },
          config: {
            initialLives: 3,
            pointsPerQR: 10,
            magnetThreshold: 50,
            magnetDuration: 5000,
            spawnRateEnemy: 60,
            spawnRateQR: 90,
            minSpeed: 3,
            maxSpeed: 8,
          },
          login: {
            requirePhoneNumber: true,
            phoneValidationPattern: "^09\\d{9}$",
            enableEmail: false,
            enableSocialLogin: false,
          },
        },
        MeepMeepProRunner: {
          type: "MeepMeepProRunner",
          colors: {
            background: "#588ffb",
            ground: "#f8af5d",
            hud: "#000000",
            accent: "#ffd700",
            particleHit: "#ff0000",
          },
          images: {
            player: "",
            hero: "",
            bgFar: "",
            bgNear: "",
            traps: [],
            phoneFrameBackground: "", // Added for phone frame background
          },
          texts: {
            title: "MeepMeep Run",
            subtitle: "بپر و امتیاز بگیر",
            scoreLabel: "امتیاز",
            levelLabel: "مرحله",
            comboLabel: "کومبو",
            startBtn: "شروع",
            helpBtn: "راهنما",
            gameOver: "پایان بازی",
            newRecordMessage: "رکورد جدید!",
            finalScore: "امتیاز نهایی",
            maxCombo: "بیشترین کومبو",
            restartBtn: "دوباره",
            rewardBtn: "جایزه",
            submitScore: "ثبت امتیاز",
            phonePrompt: "برای دریافت جایزه، شماره موبایل خود را وارد کنید:",
            phonePlaceholder: "۰۹۱۲XXXXXXX",
            submitting: "در حال ثبت...",
            submitAndRestart: "ثبت و شروع دوباره",
            cancel: "انصراف",
            howToPlay: "چطوری بازی کنیم؟",
            closeHelp: "اوکی، بریم!",
            helpContent:
              "👈 لمس <b>چپ</b> صفحه: لاین چپ<br/>👉 لمس <b>راست</b> صفحه: لاین راست<br/>👆 لمس <b>وسط</b> صفحه: پریدن<br/>🔥 کومبو بزن تا امتیازت چند برابر شه!",
          },
          gameplay: {
            lanesCount: 3,
            baseSpeed: 4,
            maxSpeed: 15,
            speedStep: 0.5,
            jumpForce: -15,
            gravity: 0.8,
            minScoreToSubmit: 100,
          },
          login: {
            requirePhoneNumber: true,
            phoneValidationPattern: "^09\\d{9}$",
            enableEmail: false,
            enableSocialLogin: false,
          },
        },
      };

      this.gameConfig = JSON.parse(JSON.stringify(DEFAULTS[selectedType]));
      this.syncToParent();
    },

    onGameTypeChange(newType) {
      this.$modal
        .yesNo(
          "با تغییر نوع بازی، تنظیمات فعلی بازنشانی می‌شوند. ادامه می‌دهید؟",
        )
        .then(() => {
          this.setInitConfig(newType);
          this.tabs = 0;
        })
        .catch(() => {
          this.gameType = this.gameConfig.type;
        });
    },

    resetToDefault() {
      this.setInitConfig(this.gameType);
      this.$toast.success("تنظیمات به حالت پیش‌فرض بازگشت");
    },

    saveAndPreview() {
      this.syncToParent();
      this.$emit("preview");
    },

    syncToParent() {
      const payload = JSON.parse(JSON.stringify(this.valueComputed));
      if (!payload.previewer) this.$set(payload, "previewer", {});
      if (!payload.previewer.game) this.$set(payload.previewer, "game", {});

      // Ensure all config sections exist
      const config = JSON.parse(JSON.stringify(this.gameConfig));
      if (!config.texts) config.texts = {};
      if (!config.images) config.images = {};
      if (!config.config) config.config = {};
      if (!config.gameplay) config.gameplay = {};
      if (!config.colors) config.colors = {};
      if (!config.login) config.login = {};

      payload.previewer.game = {
        type: this.gameConfig.type,
        config: config,
      };

      this.$emit("input", payload);
    },
  },
};
</script>
